import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Modal, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { money, paymentReads, plans as plansApi } from '../../api/fee'
import type { Plan } from '../../api/types'
import { CatalogueEditor } from '../../components/CatalogueEditor'
import { ConfirmButton, Notice, PageTitle, QueryState } from '../../components/common'
import { BillingInfoBlock, PaymentMethodInfoBlock } from '../../components/payment'
import { PayActions } from '../../components/PayActions'
import { useProject } from '../../layout/projectContext'
import { isPlanPayable, planLimits } from '../../lib/catalogue'
import { CheckoutRedirectError, redirectToCheckout, storedSessionId } from '../../lib/checkout'
import { formatDateTime, formatPrice } from '../../lib/format'
import { hasMethod } from '../../lib/paymentHelpers'
import { useMoneyAction } from '../../lib/useMoneyAction'

function statusText(plan: Plan): string {
  const sub = plan.subscription
  if (!sub) return ''
  const at = formatDateTime(sub.expiresAt)
  switch (Number(sub.status)) {
    case 1:
      return `Subscribed, will be automatically renewed at: ${at}`
    case 3:
      return `Overdue, expires at: ${at}`
    case 6:
      return `Canceled, expires at: ${at}`
    default:
      return ''
  }
}

/**
 * Port of `PlanListComponent` (`projects/:projectId/subscriptions[/:planId]`):
 * the plan catalogue with the caller's own subscription per plan, subscribe
 * (pay) / cancel / resume, and — project admins — add/edit. `:planId` opens
 * that plan's pay or view modal (deep link, as legacy).
 */
export function PlanListPage() {
  const { projectId, isAdmin } = useProject()
  const { planId } = useParams<{ planId?: string }>()
  const navigate = useNavigate()
  const list = useApiQuery(() => plansApi.search(projectId), [projectId])
  const pm = useApiQuery(() => paymentReads.method(projectId), [projectId])
  const billing = useApiQuery(() => paymentReads.billing(projectId), [projectId])
  const deepLinked = useApiQuery(
    () =>
      planId && list.data && !list.data.some((p) => p.id === planId)
        ? plansApi.view(projectId, planId)
        : Promise.resolve(null),
    [projectId, planId, list.data],
  )
  const [editing, setEditing] = useState<Plan | 'new' | null>(null)
  const actions = useMoneyAction()
  const base = `/projects/${encodeURIComponent(projectId)}/subscriptions`
  const withMethod = hasMethod(pm.data)

  const refresh = () => {
    list.refetch()
    pm.refetch()
  }

  const cancel = (plan: Plan) =>
    actions.run(`cancel-${plan.id}`, plan.id, () => money.cancelSubscription(projectId, plan.id), refresh)
  const restart = (plan: Plan) =>
    actions.run(`restart-${plan.id}`, plan.id, () => money.restartSubscription(projectId, plan.id), refresh)
  const resumeWithNewMethod = (plan: Plan) =>
    actions.run(`resume-${plan.id}`, plan.id, async () => {
      const body = { type: 'plan' as const, plans: plan.id, cause: 'setup' }
      const old = storedSessionId(projectId)
      const session = await money.createSession(projectId, old ? { ...body, oldSessionId: old } : body)
      try {
        redirectToCheckout(projectId, session)
      } catch (err) {
        if (err instanceof CheckoutRedirectError) actions.setFeedback(`resume-${plan.id}`, { tone: 'error', text: err.message })
        else throw err
      }
      return session
    })

  const rowActions = (plan: Plan) => {
    const sub = plan.subscription
    const status = Number(sub?.status ?? 0)
    return (
      <span className="portal-inline-actions">
        {isPlanPayable(plan) && (
          <Link className="portal-button-link" to={`${base}/${encodeURIComponent(plan.id)}`}>
            Subscribe
          </Link>
        )}
        {status === 1 && (
          <ConfirmButton
            label="Cancel subscription"
            pendingText="Are you sure you want to cancel this subscription?"
            busy={actions.isBusy(`cancel-${plan.id}`)}
            onConfirm={() => void cancel(plan)}
          />
        )}
        {status === 6 &&
          (withMethod ? (
            <button type="button" disabled={actions.isBusy(`restart-${plan.id}`)} onClick={() => void restart(plan)}>
              Resume subscription
            </button>
          ) : (
            <button type="button" disabled={actions.isBusy(`resume-${plan.id}`)} onClick={() => void resumeWithNewMethod(plan)}>
              Resume subscription and pay with new payment method
            </button>
          ))}
        {isAdmin && (
          <button type="button" onClick={() => setEditing(plan)}>
            Edit
          </button>
        )}
        <Notice feedback={actions.feedbackFor(`cancel-${plan.id}`)} />
        <Notice feedback={actions.feedbackFor(`restart-${plan.id}`)} />
        <Notice feedback={actions.feedbackFor(`resume-${plan.id}`)} />
      </span>
    )
  }

  const columns: Column<Plan>[] = [
    {
      key: 'name',
      label: 'Plan',
      format: (_v, plan) => (
        <>
          <Link to={`${base}/${encodeURIComponent(plan.id)}`}>{plan.name}</Link>
          {isAdmin && Boolean(plan.hidden) && <span className="portal-muted-small">(hidden)</span>}
        </>
      ),
    },
    {
      key: 'price',
      label: 'Price',
      align: 'right',
      numeric: true,
      format: (_v, plan) => formatPrice(plan.price, plan.currency),
    },
    { key: 'status', label: 'Status', sortValue: (p) => Number(p.subscription?.status ?? 0), format: (_v, p) => statusText(p) },
    { key: 'actions', label: '', sortable: false, format: (_v, p) => rowActions(p) },
  ]

  const selected = planId ? (list.data?.find((p) => p.id === planId) ?? deepLinked.data ?? null) : null
  const closeModal = () => navigate(base)

  return (
    <div className="portal-card">
      <PageTitle title="Subscriptions">
        {isAdmin && (
          <button type="button" onClick={() => setEditing('new')}>
            Add plan
          </button>
        )}
      </PageTitle>
      <QueryState loading={list.loading} error={list.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={list.data ?? []} rowKey={(p) => p.id} />
        </div>
        {list.data?.length === 0 && <p className="portal-text-muted">No plans yet.</p>}
      </QueryState>

      {selected && (
        <Modal open title={selected.name} onClose={closeModal} className="portal-modal-wide">
          <PlanModalBody
            plan={selected}
            projectId={projectId}
            payable={isPlanPayable(selected)}
            withMethod={withMethod}
            pmBlock={<PaymentMethodInfoBlock projectId={projectId} pm={pm.data} />}
            billingBlock={<BillingInfoBlock projectId={projectId} billing={billing.data} />}
            onCharged={() => {
              refresh()
            }}
            extraActions={rowActions(selected)}
          />
        </Modal>
      )}

      {editing && (
        <CatalogueEditor
          kind="plan"
          projectId={projectId}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.refetch()
          }}
        />
      )}
    </div>
  )
}

function PlanModalBody({
  plan,
  projectId,
  payable,
  withMethod,
  pmBlock,
  billingBlock,
  onCharged,
  extraActions,
}: {
  plan: Plan
  projectId: string
  payable: boolean
  withMethod: boolean
  pmBlock: ReactNode
  billingBlock: ReactNode
  onCharged: () => void
  extraActions: ReactNode
}) {
  const status = Number(plan.subscription?.status ?? 0)
  const limits = planLimits(plan).filter((l) => Number(l.value) > -1)
  return (
    <div className="fs-modal-body">
      {!payable && <p className="portal-notice portal-notice--info">{statusText(plan) || 'Not subscribed.'}</p>}
      {!(Number(plan.price) > 0) && <p className="portal-notice portal-notice--warning">This plan is not subscribable at this moment!</p>}
      <div className="portal-grid-2">
        {pmBlock}
        <div className="fs-panel">
          <h3>{payable ? 'Total to pay' : 'Monthly fee'}</h3>
          <p className="fs-amount">{formatPrice(plan.price, plan.currency)}</p>
        </div>
        {billingBlock}
        {limits.length > 0 && (
          <div className="fs-panel">
            <h3>Limits</h3>
            <dl className="portal-kv">
              {limits.map((l) => (
                <div key={l.key} className="fs-kv-row">
                  <dt>{l.description || l.key}</dt>
                  <dd className="fs-num">{l.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
      {Number(plan.price) > 0 && (payable || status === 1 || status === 3) && (
        <PayActions
          projectId={projectId}
          target={{ type: 'plan', plans: plan.id }}
          hasPaymentMethod={withMethod}
          onCharged={onCharged}
        />
      )}
      {!payable && <div className="portal-inline-actions">{extraActions}</div>}
    </div>
  )
}
